// components/TestRunner/rules/Rule00_BasicConfigurationFlags.js

/**
 * Rule00_BasicConfigurationFlags.js
 *
 * ✅ Règle 00 — Une fibre doit obligatoirement être déclarée dans la configuration tiroir et dans la base ROP
 * Type: Error
 *
 * 🧪 Tests effectués :
 * • Vérification de l'existence : hasConfigTiroir, hasRopRef
 * • Validation hasConfigTiroir = true (fibre dans Configuration des Tiroirs)
 * • Validation hasRopRef = true (fibre dans base ROP)
 * • Génération d'erreurs spécifiques selon le type de problème (undefined/null vs false)
 */

export class Rule00_BasicConfigurationFlags {
  /**
   * Appliquer la règle 00 : Flags de configuration de base
   * @param {Array} allFibers - Toutes les fibres du projet
   */
  static apply(allFibers) {
    // Analyser chaque fibre individuellement
    allFibers.forEach((fiber) => {
      const fiberAnalysis = this._analyzeFiberBasicConfig(fiber);

      // Si des flags manquants sont détectés, ajouter une erreur à la fibre
      if (fiberAnalysis.hasMissingFlags) {
        this._addError(fiber, "R00", fiberAnalysis.errorTitle, fiberAnalysis.errorMessage);
      }
    });
  }

  /**
   * Analyser les flags de base d'une fibre
   * @param {Object} fiber - Objet fibre
   * @returns {Object} Résultat de l'analyse
   */
  static _analyzeFiberBasicConfig(fiber) {
    const analysis = {
      fiberId: fiber.id,
      hasMissingFlags: false,
      errorTitle: "",
      errorMessage: "",
    };

    // Vérifier hasConfigTiroir
    const configTiroirError = this._checkConfigTiroir(fiber.hasConfigTiroir);
    if (configTiroirError) {
      analysis.hasMissingFlags = true;
      analysis.errorTitle = configTiroirError.title;
      analysis.errorMessage = configTiroirError.message;
      return analysis; // Retourner immédiatement pour cette erreur
    }

    // Vérifier hasRopRef
    const ropRefError = this._checkRopRef(fiber.hasRopRef);
    if (ropRefError) {
      analysis.hasMissingFlags = true;
      analysis.errorTitle = ropRefError.title;
      analysis.errorMessage = ropRefError.message;
      return analysis; // Retourner immédiatement pour cette erreur
    }

    return analysis;
  }

  /**
   * Vérifier la configuration des tiroirs
   * @param {*} hasConfigTiroir - Valeur de hasConfigTiroir
   * @returns {Object|null} Erreur ou null si OK
   */
  static _checkConfigTiroir(hasConfigTiroir) {
    if (hasConfigTiroir === undefined || hasConfigTiroir === null) {
      return {
        title: "Impossible d'exécuter le test - Fibre absente de la configuration des tiroirs",
        message: this._generateErrorMessage(
          "Impossible d'exécuter le test - Fibre absente de la configuration des tiroirs",
          [
            "Vérifier l'état de la fibre dans les photos des tiroirs",
            "Mettre à jour la configuration pour la fibre en question",
          ]
        ),
      };
    }

    if (hasConfigTiroir === false) {
      return {
        title: "Fibre non référencée dans la Configuration des Tiroirs",
        message: this._generateErrorMessage("Fibre non référencée dans la Configuration des Tiroirs", [
          "Enregistrer la fibre dans la Configuration des Tiroirs",
        ]),
      };
    }

    return null; // Pas d'erreur
  }

  /**
   * Vérifier la référence ROP
   * @param {*} hasRopRef - Valeur de hasRopRef
   * @returns {Object|null} Erreur ou null si OK
   */
  static _checkRopRef(hasRopRef) {
    if (hasRopRef === undefined || hasRopRef === null) {
      return {
        title: "Impossible d'exécuter le test - Fibre absente de la base ROP",
        message: this._generateErrorMessage("Impossible d'exécuter le test - Fibre absente de la base ROP", [
          "Vérifier l'état de la fibre dans la base de données ROP",
          "Mettre à jour la base ROP pour la fibre en question",
        ]),
      };
    }

    if (hasRopRef === false) {
      return {
        title: "Fibre non référencée dans la base ROP",
        message: this._generateErrorMessage("Fibre non référencée dans la base ROP", [
          "Enregistrer la fibre dans la base de données ROP",
        ]),
      };
    }

    return null; // Pas d'erreur
  }

  /**
   * Générer le message d'erreur formaté
   * @param {string} errorDescription - Description de l'erreur
   * @param {Array} actions - Liste des actions requises
   * @returns {string} Message HTML formaté
   */
  static _generateErrorMessage(errorDescription, actions) {
    let message = `<strong>⚠️ Problème détecté :</strong><br>`;
    message += `${errorDescription}<br><br>`;

    message += `<strong>⚡ Action requise :</strong><br>`;
    actions.forEach((action) => {
      message += `- ${action}<br>`;
    });

    return message;
  }

  /**
   * Ajouter une erreur au résultat d'une fibre
   */
  static _addError(fiber, rule, title, message) {
    // Initialize testResults if it doesn't exist
    if (!fiber.testResults) {
      fiber.testResults = {
        status: "success",
        hasError: false,
        messages: [],
      };
    }

    // Configure error details
    fiber.testResults.status = "error";
    fiber.testResults.hasError = true;
    fiber.testResults.messages.push({
      type: "error",
      rule,
      title,
      message,
    });
  }
}
